import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, ChevronRight, Pencil, Loader2, CalendarDays, Users } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useAllTours, useCreateTour } from '@/hooks/useActivities'
import { useOrgTourStats } from '@/hooks/useOrgTourStats'
import { useOrg } from '@/context/OrgContext'
import { useSelectedTour } from '@/context/TourContext'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { TourCard, PlanNewTourCard } from '@/components/manager/TourCard'
import { TourEditDialog } from '@/components/manager/TourEditDialog'
import { computeTourState, formatTourDates } from '@/lib/tourState'
import { formatDayHeader } from '@/lib/timezones'
import { withViewTransition } from '@/lib/viewTransition'
import { AssignFromOrgPicker } from './CrewView'
import { useTourMembers } from '@/hooks/useTourCrew'
import type { Tour } from '@/types'

// Two-faced view:
//   • <lg → mobile/tablet → keeps the original compact list + inline form.
//   • lg+ → desktop → the new rich-card grid + archived collapse.
// Each side owns its own UI state; React Query dedupes the actual data
// fetches so there's no double round-trip.

export function TourSetup() {
  return (
    <>
      <div className="lg:hidden">
        <MobileTourList />
      </div>
      <div className="hidden lg:block">
        <DesktopTourGrid />
      </div>
    </>
  )
}

// ─── Desktop: rich card grid ────────────────────────────────────────────────

function DesktopTourGrid() {
  const { activeOrg } = useOrg()
  const { data: tours = [], isLoading } = useAllTours()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setSelectedTourId } = useSelectedTour()

  const tourIds = useMemo(() => tours.map(t => t.id), [tours])
  const { data: statsMap } = useOrgTourStats(activeOrg?.id, tourIds)

  const [editingTour, setEditingTour] = useState<Tour | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [archivedOpen, setArchivedOpen] = useState(false)

  // Bucket + sort. Active tours surface "soonest next show" first so the card
  // most likely to need attention is top-left.
  const { active, archived } = useMemo(() => {
    const a: Tour[] = []
    const ar: Tour[] = []
    for (const t of tours) (t.estado === 'active' ? a : ar).push(t)

    const nextShowMs = (t: Tour): number => {
      const stats = statsMap?.get(t.id)
      if (!stats) return Number.POSITIVE_INFINITY
      const state = computeTourState(t, stats.activities)
      return state.nextActivity
        ? new Date(state.nextActivity.start_time_utc).getTime()
        : Number.POSITIVE_INFINITY
    }
    a.sort((x, y) => nextShowMs(x) - nextShowMs(y))
    ar.sort((x, y) => y.fecha_fin.localeCompare(x.fecha_fin))
    return { active: a, archived: ar }
  }, [tours, statsMap])

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['all-tours'] })
    queryClient.invalidateQueries({ queryKey: ['available-tours'] })
    queryClient.invalidateQueries({ queryKey: ['org-tour-stats'] })
  }

  function handleOpen(tour: Tour) {
    setSelectedTourId(tour.id)
    withViewTransition(() => navigate('/manager'))
  }

  async function handleArchive(tour: Tour) {
    if (!confirm(`Archive "${tour.nombre}"?`)) return
    const { error } = await supabase.from('tours').update({ estado: 'archived' }).eq('id', tour.id)
    if (error) toast.error('Failed to archive tour')
    else { toast.success('Tour archived'); invalidate() }
  }

  async function handleActivate(tour: Tour) {
    const { error } = await supabase.from('tours').update({ estado: 'active' }).eq('id', tour.id)
    if (error) toast.error('Failed to activate tour')
    else { toast.success('Tour activated'); invalidate() }
  }

  function handleNewTour() {
    setEditingTour(null)
    setDialogOpen(true)
  }

  const headline = useMemo(() => {
    if (active.length === 0 && archived.length === 0) {
      return 'No tours yet — start with one to begin scheduling.'
    }
    const parts: string[] = []
    parts.push(`${active.length} active`)
    if (archived.length > 0) parts.push(`${archived.length} archived`)
    return parts.join(' · ')
  }, [active.length, archived.length])

  return (
    <div className="space-y-8">
      {/* Slim page-head: subtitle + actions. Big "Tours" title is omitted
          because the manager tab strip already shows "Tours" as active. */}
      <div className="flex items-end justify-between gap-4 pb-1">
        <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
          {headline}
        </p>
        <Button onClick={handleNewTour} className="gap-1.5 h-9">
          <Plus className="w-3.5 h-3.5" strokeWidth={2.4} />
          New tour
        </Button>
      </div>

      {/* Active grid */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            Active · sorted by next show
          </span>
          {active.length > 0 && (
            <span className="font-mono text-[11px] text-subtle-foreground">
              {active.length} of {active.length}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-[420px] rounded-2xl bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {active.map(tour => (
              <TourCard
                key={tour.id}
                tour={tour}
                stats={statsMap?.get(tour.id)}
                onOpen={handleOpen}
                onEdit={(t) => { setEditingTour(t); setDialogOpen(true) }}
                onArchive={handleArchive}
                onActivate={handleActivate}
              />
            ))}
            {/* Always-on at the end of the active grid: discoverable CTA
                that doubles as the empty-state card when there are 0 tours. */}
            <PlanNewTourCard onClick={handleNewTour} />
          </div>
        )}
      </section>

      {/* Archived collapse */}
      {archived.length > 0 && (
        <section className="border-t border-border pt-6">
          <button
            type="button"
            onClick={() => setArchivedOpen(o => !o)}
            data-open={archivedOpen}
            className="tf-archived-toggle flex items-center justify-between w-full py-2 text-foreground"
          >
            <div className="flex items-center gap-3">
              <ChevronRight className="arrow w-3.5 h-3.5" strokeWidth={2.2} />
              <span className="text-[15px] font-semibold tracking-[-0.01em]">Archived</span>
              <span className="font-mono text-[11px] text-subtle-foreground tracking-wide">
                {archived.length} tour{archived.length === 1 ? '' : 's'}
              </span>
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
              {archivedOpen ? 'Hide' : 'Show'}
            </span>
          </button>

          <div className={`tf-archived-list ${archivedOpen ? 'is-open' : ''}`}>
            <div className="tf-archived-list-inner pt-4">
              {archived.map(t => {
                const stats = statsMap?.get(t.id)
                const state = stats ? computeTourState(t, stats.activities) : null
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleOpen(t)}
                    className="tf-archived-row text-left w-full"
                  >
                    <span className="ar-name truncate">{t.nombre}</span>
                    <span className="ar-meta">{formatTourDates(t)}</span>
                    <span className="ar-meta">
                      {state ? `${state.showDays} show${state.showDays === 1 ? '' : 's'}` : '—'}
                    </span>
                    <span className="ar-meta">
                      {stats ? `${stats.crew.length} crew` : '—'}
                    </span>
                    <ChevronRight className="w-4 h-4 text-subtle-foreground" />
                  </button>
                )
              })}
            </div>
          </div>
        </section>
      )}

      <TourEditDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) setEditingTour(null)
        }}
        tour={editingTour}
      />
    </div>
  )
}

// ─── Mobile: original compact list + inline form (unchanged behavior) ───────

function MobileTourList() {
  const { data: tours = [], isLoading } = useAllTours()
  const createTour = useCreateTour()
  const queryClient = useQueryClient()
  const { activeOrg } = useOrg()

  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const [nombre, setNombre] = useState('')
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [saving, setSaving] = useState(false)

  function openCreate() {
    setEditingId(null)
    setNombre('')
    setFechaInicio('')
    setFechaFin('')
    setShowCreate(true)
  }

  function openEdit(tour: Tour) {
    setShowCreate(false)
    setEditingId(tour.id)
    setNombre(tour.nombre)
    setFechaInicio(tour.fecha_inicio)
    setFechaFin(tour.fecha_fin)
  }

  function closeForm() {
    setShowCreate(false)
    setEditingId(null)
  }

  function invalidateTours() {
    queryClient.invalidateQueries({ queryKey: ['all-tours'] })
    queryClient.invalidateQueries({ queryKey: ['available-tours'] })
    queryClient.invalidateQueries({ queryKey: ['org-tour-stats'] })
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!nombre.trim() || !fechaInicio || !fechaFin || !activeOrg) return
    try {
      const newTour = await createTour.mutateAsync({
        org_id: activeOrg.id,
        nombre: nombre.trim(),
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      })
      // Wait for the tours list to include the new row before transitioning
      // to edit mode — otherwise the inline edit form has no tour to render.
      await queryClient.refetchQueries({ queryKey: ['all-tours', activeOrg.id] })
      toast.success('Tour created — now assign crew')
      setShowCreate(false)
      setEditingId(newTour.id)
    } catch {
      toast.error('Failed to create tour')
    }
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault()
    if (!nombre.trim() || !fechaInicio || !fechaFin || !editingId) return
    setSaving(true)
    const { error } = await supabase
      .from('tours')
      .update({ nombre: nombre.trim(), fecha_inicio: fechaInicio, fecha_fin: fechaFin })
      .eq('id', editingId)
    setSaving(false)
    if (error) toast.error('Failed to update tour')
    else { toast.success('Tour updated'); invalidateTours(); closeForm() }
  }

  async function handleArchive(tourId: string, tourName: string) {
    if (!confirm(`Archive "${tourName}"?`)) return
    const { error } = await supabase.from('tours').update({ estado: 'archived' }).eq('id', tourId)
    if (error) toast.error('Failed to archive tour')
    else { toast.success('Tour archived'); invalidateTours() }
  }

  async function handleActivate(tourId: string) {
    const { error } = await supabase.from('tours').update({ estado: 'active' }).eq('id', tourId)
    if (error) toast.error('Failed to activate tour')
    else { toast.success('Tour activated'); invalidateTours() }
  }

  const isFormLoading = createTour.isPending || saving

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-foreground">Tours</h2>
        {!showCreate && (
          <Button size="sm" variant="outline" onClick={openCreate} className="gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            New Tour
          </Button>
        )}
      </div>

      {showCreate && (
        <TourForm
          label="New Tour"
          nombre={nombre} setNombre={setNombre}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
          fechaFin={fechaFin} setFechaFin={setFechaFin}
          loading={isFormLoading}
          onSubmit={handleCreate}
          onCancel={closeForm}
          submitLabel="Create"
        />
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map(i => (
            <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : tours.length === 0 && !showCreate ? (
        <div className="text-center py-10">
          <p className="text-sm text-muted-foreground">No tours yet. Create your first one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tours.map(tour => (
            editingId === tour.id ? (
              <TourForm
                key={tour.id}
                label="Edit Tour"
                nombre={nombre} setNombre={setNombre}
                fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
                fechaFin={fechaFin} setFechaFin={setFechaFin}
                loading={isFormLoading}
                onSubmit={handleUpdate}
                onCancel={closeForm}
                submitLabel="Save"
                tourId={tour.id}
                orgId={activeOrg?.id}
              />
            ) : (
              <div
                key={tour.id}
                className="rounded-xl border border-border bg-card p-4 flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm text-foreground truncate">{tour.nombre}</p>
                    <Badge
                      variant={tour.estado === 'active' ? 'default' : 'secondary'}
                      className="text-[10px] h-4 px-1.5 shrink-0"
                    >
                      {tour.estado}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDayHeader(tour.fecha_inicio)} → {formatDayHeader(tour.fecha_fin)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(tour)}
                    className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Edit tour"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {tour.estado === 'active' ? (
                    <Button size="sm" variant="ghost" className="text-xs h-7"
                      onClick={() => handleArchive(tour.id, tour.nombre)}>
                      Archive
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="text-xs h-7"
                      onClick={() => handleActivate(tour.id)}>
                      Activate
                    </Button>
                  )}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  )
}

// ── Mobile inline form (unchanged from the original implementation) ─────────

function TourForm({ label, nombre, setNombre, fechaInicio, setFechaInicio, fechaFin, setFechaFin, loading, onSubmit, onCancel, submitLabel, tourId, orgId }: {
  label: string
  nombre: string; setNombre: (v: string) => void
  fechaInicio: string; setFechaInicio: (v: string) => void
  fechaFin: string; setFechaFin: (v: string) => void
  loading: boolean
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
  submitLabel: string
  tourId?: string
  orgId?: string
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-xl border border-border bg-card overflow-hidden animate-in fade-in zoom-in-[0.96] dur-slow ease-out-soft origin-top"
      noValidate
    >
      <div className="px-4 pt-4 pb-2">
        <p className="text-xs font-semibold text-primary uppercase tracking-wider">{label}</p>
      </div>
      <div className="px-4 pb-4 space-y-4">
        <Input
          placeholder="Tour name"
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          className="h-12 text-base font-semibold border-none bg-muted/50 rounded-xl px-4 placeholder:text-muted-foreground/60 focus-visible:ring-1"
          required
        />
        <div className="rounded-xl border border-border bg-background overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-3 pb-2">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Dates</span>
          </div>
          <div className="px-4 pb-3 space-y-3">
            <div className="flex items-center gap-3 min-h-[2.5rem]">
              <span className="text-sm text-foreground shrink-0">Start</span>
              <div className="flex-1 flex justify-end">
                <Input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)}
                  className="h-9 text-sm border-none bg-transparent p-0 text-right focus-visible:ring-0" required />
              </div>
            </div>
            <div className="flex items-center gap-3 min-h-[2.5rem]">
              <span className="text-sm text-foreground shrink-0">End</span>
              <div className="flex-1 flex justify-end">
                <Input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)}
                  className="h-9 text-sm border-none bg-transparent p-0 text-right focus-visible:ring-0" required />
              </div>
            </div>
          </div>
        </div>
        {tourId && orgId && (
          <TourCrewSection tourId={tourId} orgId={orgId} />
        )}
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1 h-11 rounded-xl">
            {tourId ? 'Done' : 'Cancel'}
          </Button>
          <Button type="submit" disabled={loading || !nombre.trim()} className="flex-1 h-11 rounded-xl font-semibold">
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  )
}

function TourCrewSection({ tourId, orgId }: { tourId: string; orgId: string }) {
  const { data: crew = [] } = useTourMembers(tourId)
  const tourMemberIds = useMemo(() => new Set(crew.map(m => m.user_id)), [crew])
  const count = crew.length

  return (
    <div className="rounded-xl border border-border bg-background overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <Users className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Crew
        </span>
        <span className="text-xs text-muted-foreground/70">· {count}</span>
      </div>
      <div className="px-4 pb-4">
        <AssignFromOrgPicker
          orgId={orgId}
          tourId={tourId}
          tourMemberIds={tourMemberIds}
        />
      </div>
    </div>
  )
}
